import StageQr from "@/components/StageQr";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { printElement } from "@/lib/print";
import { useLiveRefresh } from "@/lib/live";
import { useAuth, hasRole, isAdmin } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ORANGE_BTN, adjustedRequired, qty } from "@/components/stores/storesUi";

// "2. STORES · Issuing of Material", the paper form PF005/F02-00, wherever a BPR's stores
// details are shown: the job's Stores Issue stage, its Materials tab and the stores keeper's
// Jobs queue. Quantities come from the stores ledger (what stores actually issued and what
// came back); the rest is the stage record `stages.stores_issue.data`.
const MIN_ROWS = 4;

const line = "border border-neutral-700";
const cell = `${line} px-2 py-1.5 text-xs align-middle`;
const input = "w-full bg-transparent outline-none text-xs font-medium placeholder:text-neutral-300 focus:bg-orange-50 disabled:text-neutral-800";

/** The BPR's issue rows: one per issue entry, with what came back on its roll. Corrected
 * (reversed) entries are not material on the floor. */
export function issueRows(ctx) {
  const live = (ctx?.lines || []).filter((l) => !l.reverses && !l.reversed_by);
  const back = {};
  live.filter((l) => l.txn_type === "return").forEach((l) => { back[l.roll_no] = (back[l.roll_no] || 0) + l.qty; });
  const counted = new Set();
  return live.filter((l) => l.txn_type === "issue").map((l, i) => {
    const first = !counted.has(l.roll_no);
    counted.add(l.roll_no);
    // The first row's "Required" is scaled for the width actually issued, not the flat planned figure —
    // a narrower roll than planned needs more metres, a wider one needs the same (just extra trim). Same
    // rule the Issue dialog itself previews before committing, so the printed sheet never disagrees with it.
    const adj = i === 0 ? adjustedRequired(ctx.job, ctx.required, l.width) : null;
    return {
      id: l.id,
      required: i === 0 ? (adj ? adj.metres : ctx.required) : null,
      issued: l.qty,
      returned: first ? back[l.roll_no] ?? null : null,
      qc: l.qc_ar_no || "",
      by: [l.created_by, l.date].filter(Boolean).join(" · "),
      // Header details, for when the stage record has none of its own (e.g. a completed stage).
      item_name: l.item_name, width: l.width, grn: l.grn_no, lot: l.master_lot, supplier: l.supplier, by_name: l.created_by,
    };
  });
}

/** The sheet itself. `data` is the stage record; `set(key, value)` changes it. */
export function SheetBody({ ctx, data, set, readOnly, pending = [] }) {
  const job = ctx.job || {};
  const rows = issueRows(ctx);
  const received = data.issue_received || {};
  const issuedBy = data.issue_by || {};
  // What was saved on the stage wins; otherwise what stores issued (so a locked, completed
  // stage still shows its GRN, lot and width), then the job card.
  // A roll just added to "Issuing" (not posted yet) already carries its own GRN/lot/width/supplier —
  // the header fields shouldn't wait for the ledger entry to show them.
  const headRows = [...rows, ...pending];
  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "") ?? "";
  const joined = (key, list = rows) => [...new Set(list.map((r) => r[key]).filter(Boolean))].join(", ");
  const head = {
    paper_type: pick(data.paper_type, rows[0]?.item_name, pending[0]?.item_name, job.substrate_type),
    gsm: pick(data.gsm, job.gsm),
    paper_width_mm: pick(data.paper_width_mm, rows[0]?.width, pending[0]?.width),
    manufactured_by: pick(data.manufactured_by, data.paper_supplier, job.manufactured_by, joined("supplier", headRows)),
    grn_number: pick(data.grn_number, joined("grn", headRows)),
    lot_id: pick(data.lot_id, joined("lot", headRows)),
    issued_by: pick(data.issued_by, joined("by_name")),
  };

  // A render helper, not a component: a component defined here would remount (and drop focus) on every keystroke.
  const field = (label, k, { value = head[k], editable = true, wide = false } = {}) => (
    <div key={k} className={`flex items-end gap-2 ${wide ? "md:col-span-2" : ""}`}>
      <span className="text-xs whitespace-nowrap">{label}</span>
      <input className={`${input} border-b border-dotted border-neutral-600 pb-0.5`} value={value ?? ""} disabled={readOnly || !editable}
        onChange={(e) => set(k, e.target.value)} data-testid={`sheet-${k}`} />
    </div>
  );

  return (
    <div className="bg-white border border-neutral-700 text-neutral-900" data-testid="stores-sheet">
      {/* Only on paper: the form itself carries no BPR number, the printout does. */}
      <div className="print-only px-4 py-2 text-xs border-b border-neutral-700" data-testid="sheet-print-head">
        <div className="flex justify-between items-start gap-3">
          <div>
            <div className="font-semibold">MedPack · BPR {ctx.bpr_no}</div>
            <div>{[job.customer_name, job.product_name, job.quantity ? `Qty ${job.quantity} ${job.unit || ""}` : ""].filter(Boolean).join(" · ")}</div>
          </div>
          <div className="flex items-start gap-2">
            <StageQr jobId={ctx.job_id || job.id} stage="stores_issue" />
            <span className="font-semibold">Page 02 of 11 · Printed {new Date().toLocaleDateString("en-IN")}</span>
          </div>
        </div>
      </div>
      {/* On screen the QR sits here; on paper the print header above carries it. */}
      <div className="no-print flex items-center justify-end gap-2 px-3 pt-2 text-[10px] text-neutral-500">
        <span>Scan to open this job at Stores</span>
        <StageQr jobId={ctx.job_id || job.id} stage="stores_issue" />
      </div>
      <div className="text-center font-bold text-sm tracking-wide py-2 border-b border-neutral-700">2. STORES</div>
      <div className="text-center font-semibold text-sm py-1.5 border-b border-neutral-700 bg-neutral-50">Issuing of Material</div>

      <div className="p-4 space-y-3">
        <div className="flex gap-4 text-xs">
          <span className="font-semibold">2.1</span>
          <span>Issue the label stock according to received order.</span>
        </div>
        <div className="pl-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {field("Substrate:", "paper_type", { wide: true })}
          {field("GSM of paper:", "gsm")}
          {field("Roll width (mm):", "paper_width_mm")}
          {field("Manufactured by:", "manufactured_by")}
          {field("Product code:", "product_code", { value: job.product_code, editable: false })}
          {field("GRN No.:", "grn_number")}
          {field("Batch No./Lot No.:", "lot_id")}
        </div>

        <div className="pl-8 overflow-x-auto">
          <table className="w-full border-collapse" data-testid="sheet-quantity">
            <thead>
              <tr>
                <th colSpan={3} className={`${cell} text-center font-semibold`}>Quantity</th>
                <th rowSpan={2} className={`${cell} text-center font-semibold`}>Q.C. A.R.<br />number</th>
                <th rowSpan={2} className={`${cell} text-center font-semibold`}>Issued<br />by &amp; Date<br />(Stores)</th>
                <th rowSpan={2} className={`${cell} text-center font-semibold`}>Received<br />by &amp; Date<br />(Production)</th>
              </tr>
              <tr>
                {["Required Quantity", "Issued Quantity", "Returned Quantity"].map((h) => (
                  <th key={h} className={`${cell} text-center font-semibold`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...rows, ...pending.map((p) => ({ ...p, pending: true })),
                ...Array.from({ length: Math.max(MIN_ROWS - rows.length - pending.length, 0) }, () => null)].map((r, i) => (
                <tr key={r?.id || r?.key || `blank-${i}`} className={`h-9 ${r?.pending ? "bg-amber-50 italic" : ""}`}
                  data-testid={r ? (r.pending ? `sheet-pending-${r.key}` : `sheet-row-${r.id}`) : undefined}>
                  {(() => { const rowKey = r?.id || r?.key || `blank-${i}`; return <>
                  <td className={`${cell} text-right font-mono-num`}>
                    {(() => {
                      // Required = Planning's good + waste + excess metres (routes/stores.py _required_m),
                      // shown from the moment the sheet opens. Only the first row ever carries it.
                      const need = r?.required ?? (i === 0
                        ? (r?.pending ? adjustedRequired(ctx.job, ctx.required, r?.width)?.metres ?? ctx.required : ctx.required)
                        : null);
                      return need ? `${qty(need, 0)} m` : "";
                    })()}
                  </td>
                  <td className={`${cell} text-right font-mono-num`}>{r ? `${qty(r.issued)} m` : ""}</td>
                  <td className={`${cell} text-right font-mono-num`}>{r?.returned ? `${qty(r.returned)} m` : ""}</td>
                  <td className={`${cell} text-center font-mono-num whitespace-nowrap`}>{r?.qc || ""}</td>
                  <td className={cell}>
                    {/* A pending row (not yet actually posted) never shows an issuer — there isn't one
                        yet — only what's typed here manually; a real posted row still auto-shows who
                        the system recorded, same as before, editable to correct it. */}
                    <input className={input} value={issuedBy[rowKey] ?? (r?.pending ? "" : r?.by) ?? ""} disabled={readOnly}
                      placeholder={readOnly ? "" : r?.pending ? "to be issued" : "name, date"}
                      onChange={(e) => set("issue_by", { ...issuedBy, [rowKey]: e.target.value })} data-testid={`sheet-issuedby-${rowKey}`} />
                  </td>
                  <td className={cell}>
                    <input className={input} value={received[rowKey] ?? ""} disabled={readOnly} placeholder={readOnly ? "" : "name, date"}
                      onChange={(e) => set("issue_received", { ...received, [rowKey]: e.target.value })} data-testid={`sheet-received-${rowKey}`} />
                  </td>
                  </>; })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border-t border-neutral-700 p-4">
        <div className="border border-neutral-700">
          <div className="px-3 py-1.5 text-xs font-bold border-b border-neutral-300">REMARKS:</div>
          <textarea className={`${input} min-h-16 px-3 py-2 resize-y`} value={data.remarks ?? ""} disabled={readOnly}
            onChange={(e) => set("remarks", e.target.value)} data-testid="sheet-remarks" />
          <div className="flex items-end justify-end gap-2 px-3 pb-2 pt-4 text-xs">
            <span className="whitespace-nowrap">Done by (stores Incharge):</span>
            <input className={`${input} border-b border-dotted border-neutral-600 w-56 text-right`} value={head.issued_by} disabled={readOnly}
              onChange={(e) => set("issued_by", e.target.value)} data-testid="sheet-issued_by" />
          </div>
        </div>
      </div>
      <div className="px-4 pb-2 text-[11px] text-neutral-600">FORMAT No. PF005/F02-00</div>
    </div>
  );
}

/** The sheet for one BPR on its own: loads it, follows stores changes live, and lets
 * stores / production fill in the parts that are theirs (Save posts to the stage record). */
export default function StoresSheet({ bpr }) {
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const card = useRef(null);

  const load = () => api.get(`/stores/bpr/${encodeURIComponent(bpr)}`)
    .then(({ data }) => { setCtx(data); setError(null); })
    .catch((e) => setError(formatApiError(e)));
  useEffect(() => { setDraft({}); load(); }, [bpr]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh(["stores", "jobs"], load);

  if (error) return <div className="text-sm text-neutral-500 py-6 text-center">{error}</div>;
  if (!ctx) return <div className="label-caps py-6 text-center">Loading…</div>;

  const completed = !!ctx.stage?.completed_at;
  // A completed stage is locked, except for the admin, who can always correct a BPR's sheet.
  const readOnly = (completed && !isAdmin(user)) || !hasRole(user, "production_manager");
  const data = { ...(ctx.stage?.data || {}), ...draft };
  const dirty = Object.keys(draft).length > 0;
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/jobs/${ctx.job.id}/stages/stores_issue`, { data: draft, complete: false });
      setDraft({});
      toast.success("Stores sheet saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  // Same finish as the Stage form: save the sheet, complete the Stores stage, the job moves on.
  const complete = async () => {
    if (!window.confirm(`Complete the Stores stage of ${bpr}? The sheet is locked, the job moves to In Production and Printing unlocks.`)) return;
    setSaving(true);
    try {
      if (dirty) await api.post(`/jobs/${ctx.job.id}/stages/stores_issue`, { data: draft, complete: false });
      await api.post(`/jobs/${ctx.job.id}/stages/stores_issue`, { data: {}, complete: true });
      setDraft({});
      toast.success(`${bpr}: Stores stage completed, job moved to In Production`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="stores-sheet-card" ref={card}>
      <SheetBody ctx={ctx} data={data} set={(k, v) => setDraft((d) => ({ ...d, [k]: v }))} readOnly={readOnly} />
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500">
        <span>
          {completed ? (readOnly ? "This stage is completed; the sheet is locked." : "This stage is completed; as admin you can still edit the sheet.")
            : readOnly ? "View only." : "Quantities come from the stores ledger; fill the rest and save."}
        </span>
        <Button size="sm" variant="outline" className="rounded-sm gap-1.5 ml-auto" onClick={() => printElement(card.current.querySelector('[data-testid="stores-sheet"]'))} data-testid="sheet-print">
          <Printer size={13} /> Print
        </Button>
        {!readOnly && (
          <span className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-sm" disabled={!dirty || saving} onClick={save} data-testid="sheet-save">
              Save sheet
            </Button>
            {!completed && (
            <Button size="sm" className={ORANGE_BTN} disabled={saving || !(ctx.net_issued > 0)} onClick={complete} data-testid="sheet-complete"
              title={!(ctx.net_issued > 0) ? "Nothing has been issued to this BPR yet" : undefined}>
              Save & complete stage
            </Button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

/** The same sheet in a dialog (stores keeper's Jobs queue). */
export function StoresSheetDialog({ bpr, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto rounded-sm">
        <DialogHeader><DialogTitle className="font-mono-num">BPR {bpr}</DialogTitle></DialogHeader>
        <StoresSheet bpr={bpr} />
      </DialogContent>
    </Dialog>
  );
}
